import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import type { AbstractControl, UntypedFormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { switchMap, tap } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { OfferPromoteJobBoardsHotspot } from '@recruitee/integrations-types';
import type { DialogComponentI } from '@recruitee/user-interface';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  IconButtonComponent,
  IconComponent,
  LabelComponent,
  RtStatefulComponent,
  RtTooltip,
} from '@recruitee/user-interface';

import { CurrentHotspotToken } from '../../../client/integrations-client.module';
import { Destroyable, HttpErrorHandlerService, takeUntilDestroyed } from '../../../common';
import { IntegrationEventActions } from '../../../common/events/events';
import { ErrorMessagesWrapperComponent } from '../../../misc/error-messages-wrapper/error-messages-wrapper.component';
import type { EditPostingForm, Posting } from '../types';
import { WerkenInEenHotelApiService } from '../werken-in-een-hotel-api';
import {
  initializePostingFormGroup,
  WerkenInEenHotelPostingFormComponent,
} from '../werken-in-een-hotel-posting-form/werken-in-een-hotel-posting-form.component';

type State = {
  posting: Posting;
  saving: boolean;
  removing: boolean;
  errors: string[];
  errorFields: { [field: string]: string[] };
};

type DialogData = {
  posting: Posting;
};

const valueIfDirty = (control: AbstractControl) => (control.dirty ? control.value : undefined);

@Component({
  selector: 'rti-werken-in-een-hotel-edit-posting-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './werken-in-een-hotel-edit-posting-dialog.component.html',
  styleUrls: ['./werken-in-een-hotel-edit-posting-dialog.component.less'],
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    ErrorMessagesWrapperComponent,
    IconButtonComponent,
    IconComponent,
    LabelComponent,
    RtTooltip,
    WerkenInEenHotelPostingFormComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class WerkenInEenHotelEditPostingDialogComponent
  extends RtStatefulComponent<State>
  implements DialogComponentI<DialogData, boolean>
{
  public postingForm: UntypedFormGroup;

  constructor(
    public dialog: DialogRef,
    private i18n: I18nService,
    private dialogService: DialogService,
    private httpErrorHandlerService: HttpErrorHandlerService,
    cdRef: ChangeDetectorRef,
    private werkenInEenHotelApi: WerkenInEenHotelApiService,
    private store: Store,
    @Inject(CurrentHotspotToken)
    private currentHotspot: OfferPromoteJobBoardsHotspot,
  ) {
    super(cdRef);

    this.createState({
      posting: null!,
      saving: false,
      removing: false,
      errors: [],
      errorFields: {},
    });
  }

  public ngOnInit(): void {
    this.postingForm.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(() => this.cdRef.detectChanges());
  }

  public setDialogData(data: DialogData): void {
    this.postingForm = initializePostingFormGroup(data.posting);
    this.setState({ ...this.state, posting: data.posting });
  }

  public save(): void {
    this.setState({ ...this.state, saving: true });

    this.werkenInEenHotelApi
      .patch(`posting/${this.state.posting.id}`, this.editPostingFormData)
      .subscribe(
        (_data: { posting: Posting }) => {
          this.setState({ ...this.state, saving: false });

          this.store.dispatch(
            IntegrationEventActions.WerkenInEenHotelPostingEdited({
              payload: { context: { hotspot: this.currentHotspot } },
            }),
          );

          this.dialog.close();
        },
        ({ error }) => {
          const errorFields = error.errorFields;
          if (errorFields && (errorFields.mainVacancyPhoto || errorFields.photos)) {
            this.setState({ ...this.state, saving: false, errorFields });
          } else {
            this.setState({ ...this.state, saving: false, errors: error.error });
          }
        },
      );
  }

  public remove(): void {
    this.dialogService
      .confirm({
        title: this.i18n.translate('rti.job_boards.edit_posting.remove'),
        message: this.i18n.translate('rti.job_boards.edit_posting.remove_confirmation'),
      })
      .pipe(
        filterBoolean,
        tap(() => {
          this.setState({ ...this.state, removing: true });
        }),
        switchMap(() => this.werkenInEenHotelApi.delete(`posting/${this.state.posting.id}`)),
        tap(
          () => {
            this.setState({ ...this.state, removing: false });

            this.store.dispatch(
              IntegrationEventActions.WerkenInEenHotelPostingDeleted({
                payload: { context: { hotspot: this.currentHotspot } },
              }),
            );

            this.dialog.close();
          },
          errorResponse => {
            this.httpErrorHandlerService.default(errorResponse);
            this.setState({ ...this.state, removing: false });
          },
        ),
      )
      .subscribe(() => {
        this.dialog.close();
      });
  }

  public close(): void {
    this.dialog.close();
  }

  public get formValid(): boolean {
    return (
      this.postingForm.valid &&
      (!this.postingForm.controls.mainVacancyPhoto.dirty || this.postingForm.value.mainVacancyPhoto)
    );
  }

  private get editPostingFormData(): FormData {
    const form: EditPostingForm = {
      locationId: valueIfDirty(this.postingForm.controls.locationId),
      contactId: valueIfDirty(this.postingForm.controls.contactId),
      functionCharacteristicId: valueIfDirty(this.postingForm.controls.functionCharacteristicId),
      employmentIds: valueIfDirty(this.postingForm.controls.employmentIds),
      mainFunctionCategoryId: valueIfDirty(this.postingForm.controls.mainFunctionCategoryId),
      extraFunctionCategoryId: valueIfDirty(this.postingForm.controls.extraFunctionCategoryId),
      publishOnOtherJobBoards: valueIfDirty(this.postingForm.controls.publishOnOtherJobBoards),
      youtubeUrl: this.postingForm.controls.youtubeUrl.dirty
        ? this.postingForm.controls.youtubeUrl.value || null
        : undefined,
      mainVacancyPhoto: valueIfDirty(this.postingForm.controls.mainVacancyPhoto),
      additionalPhotosToDrop: valueIfDirty(this.postingForm.controls.additionalPhotosToDrop),
      additionalPhotosToAdd: valueIfDirty(this.postingForm.controls.additionalPhotosToAdd),
    };

    return Object.keys(form)
      .filter((key: keyof EditPostingForm) => form[key] !== undefined)
      .reduce((formData, key: keyof EditPostingForm) => {
        const value = form[key];

        if (Array.isArray(value)) {
          const formDataKey = `${key}[]`;

          value.forEach((val: string | File) => {
            formData.append(formDataKey, val);
          });
        } else {
          formData.append(key, <string | File>value);
        }

        return formData;
      }, new FormData());
  }
}
